import api from "app/lib/api";
import { Link } from "react-router-dom";
import { textFilter } from "utils/table";
import { getFormattedDate } from "utils/date";
import React, { useState } from "react";
import CustomTable from "components/common/table/CustomTable";
import { Drawer, TextField, InputAdornment, IconButton } from "@mui/material";
import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";
import useDebounce from "app/hooks/useDebounce";
import CopyToClipboard from "components/common/copy/CopyToClipboard";

const AdtSidebar = ({ open, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  const handleSearchTextChange = useDebounce(async (value) => {
    if (value?.length >= 3) {
      setIsLoading(true);
      try {
        const data = await api.get(`/adt/get/${value}`);
        setTableData(data);
      } catch (err) {
        console.error("Error fetching ADTs:", err);
      } finally {
        setIsLoading(false);
      }
    } else {
      setTableData([]);
    }
  }, 500);

  const columns = [
    {
      header: "Name",
      filterVariant: "text",
      size: 80,
      filterFn: textFilter,
      Cell: ({ row }) => {
        const rowData = row?.original;
        return `${rowData?.firstName || ""} ${rowData?.lastName || ""}`.trim();
      },
    },
    {
      header: "Report ID",
      accessorKey: "report_id",
      filterVariant: "text",
      size: 140,
      filterFn: textFilter,
      Cell: ({ row }) => {
        const rowData = row?.original;
        return rowData?.report_id ? (
          <Link
            to={`/patient/report/${rowData.report_id}`}
            target="_blank"
            style={{ textDecoration: "none" }}
          >
            {rowData.report_id}
          </Link>
        ) : (
          "-"
        );
      },
    },
    {
      header: "DOB",
      accessorKey: "dob",
      size: 80,
      Cell: ({ row }) => {
        const rowData = row?.original;
        return rowData?.dob ? getFormattedDate(rowData?.dob, "MM/DD/YYYY") : "";
      },
    },
    {
      header: "MRN",
      accessorKey: "mrn",
      size: 70,
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "CSN",
      accessorKey: "csn",
      filterVariant: "text",
      size: 80,
      filterFn: textFilter,
      Cell: ({ row }) => {
        const rowData = row?.original;
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: "4px",
              }}
            >
              <span>{rowData?.csn || ""}</span>
              {rowData?.csn && <CopyToClipboard value={rowData?.csn || ""} />}
            </div>
          </div>
        );
      },
    },
    {
      header: "ADT Date",
      accessorKey: "adtDate",
      filterVariant: "date",
      size: 80,
      Cell: ({ row }) => {
        const rowData = row?.original;
        return rowData?.adtDate
          ? getFormattedDate(rowData?.adtDate, "MM/DD/YYYY")
          : "";
      },
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1300,
        "& .MuiDrawer-paper": {
          width: "60%",
          padding: "20px",
        },
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h2>Browse ADT</h2>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <TextField
        size="small"
        placeholder="Search MRN"
        onChange={(e) => handleSearchTextChange(e.target.value)}
        style={{ marginBottom: "20px", width: "406px" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <CustomTable
        data={tableData}
        columns={columns}
        options={{
          enableRowSelection: false,
          state: {
            isLoading,
            showColumnFilters: false,
            showGlobalFilter: false,
          },
          tablePageSize: 10,
          rowsPerPageOptions: [10, 25, 50],
        }}
      />
    </Drawer>
  );
};

export default AdtSidebar;
